<script setup lang="ts">
// A visual album full screen (HRTH-164.3, 164.6): photos decrypted on this device (the
// preview shows meanwhile) and videos playing in place; swipe or arrow keys to the next and
// previous one, ⋯ for its actions.
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import type { PlayResponse } from '../api/types'
import { entryTitle } from '../lib/libraryEntries'
import { entryImage, entryRef, photoThumb, type OpenEntry } from '../lib/libraryPlay'
import { usePlayer } from '../stores/player'
import { useSession } from '../stores/session'
import Icon from './Icon.vue'
import MediaElement from './MediaElement.vue'
import ViewerFrame from './ViewerFrame.vue'

const props = defineProps<{ library: string; photos: OpenEntry[]; start: number; album?: string }>()
const emit = defineEmits<{ close: []; more: [OpenEntry] }>()
const session = useSession()

const at = ref(props.start)
const cur = computed(() => props.photos[at.value] ?? null)
const thumb = ref<string | null>(null)
const full = ref<string | null>(null)
const video = ref<PlayResponse | null>(null)
const failed = ref<string | null>(null)

watch(
  cur,
  async (e) => {
    thumb.value = null
    full.value = null
    video.value = null
    failed.value = null
    if (!e) return emit('close')
    if (e.meta.kind === 'video') {
      // The bar's audio would play over it.
      usePlayer().stop()
      try {
        const p = await session.playLibrary(entryRef(props.library, e))
        if (cur.value === e) video.value = p
      } catch (err) {
        if (cur.value === e) failed.value = (err as Error).message
      }
      return
    }
    void photoThumb(session.api, e).then((u) => cur.value === e && (thumb.value = u))
    const url = await entryImage(session.api, e, 'original')
    if (cur.value === e) full.value = url
  },
  { immediate: true },
)

const go = (d: number) => {
  const n = at.value + d
  if (n >= 0 && n < props.photos.length) at.value = n
}

function onKey(e: KeyboardEvent): void {
  // Arrows on a video's controls seek it.
  if ((e.target as HTMLElement | null)?.closest?.('[data-testid="media-controls"]')) return
  if (e.key === 'ArrowRight') go(1)
  else if (e.key === 'ArrowLeft') go(-1)
}
onMounted(() => window.addEventListener('keydown', onKey))
onBeforeUnmount(() => window.removeEventListener('keydown', onKey))

let touchX: number | null = null
function onTouchStart(e: TouchEvent): void {
  // Dragging a video's seek bar isn't a swipe.
  const onControls = (e.target as HTMLElement | null)?.closest('[data-testid="media-controls"]')
  touchX = e.touches.length === 1 && !onControls ? e.touches[0]!.clientX : null
}
function onTouchEnd(e: TouchEvent): void {
  if (touchX === null) return
  const dx = e.changedTouches[0]!.clientX - touchX
  touchX = null
  if (Math.abs(dx) > 50) go(dx < 0 ? 1 : -1)
}
</script>

<template>
  <ViewerFrame v-if="cur" :title="entryTitle(cur.meta)" @close="emit('close')">
    <template #sub>
      {{ at + 1 }} / {{ photos.length }}<template v-if="album"> · {{ album }}</template><template v-if="cur.meta.tags?.taken"> · {{ cur.meta.tags.taken }}</template>
    </template>
    <template #actions>
      <button class="flex h-11 w-11 items-center justify-center text-white/80 hover:text-white" :aria-label="`More for ${entryTitle(cur.meta)}`" data-testid="photo-more" @click="emit('more', cur)">
        <Icon name="more" :size="5" />
      </button>
    </template>
    <div class="relative flex min-h-0 flex-1 items-center justify-center" @touchstart.passive="onTouchStart" @touchend="onTouchEnd">
      <div v-if="cur.meta.kind === 'video'" class="w-full max-w-4xl px-2" data-testid="viewer-video">
        <MediaElement v-if="video" :key="video.url" :play="video" kind="video" autoplay :reload="() => session.playLibrary(entryRef(library, cur!))" />
        <span v-else class="mono block text-center text-white/60">{{ failed ?? 'Decrypting…' }}</span>
      </div>
      <img v-else-if="full || thumb" :src="full ?? thumb ?? ''" :alt="entryTitle(cur.meta)" class="max-h-full max-w-full object-contain" data-testid="file-preview-image" />
      <span v-else class="mono text-white/60">Decrypting…</span>
      <button
        v-if="at > 0"
        class="absolute inset-y-0 left-0 flex w-16 items-center justify-center text-white/60 hover:text-white max-md:hidden"
        aria-label="Previous photo"
        data-testid="photo-previous"
        @click="go(-1)"
      >
        <Icon name="left" :size="6" />
      </button>
      <button
        v-if="at < photos.length - 1"
        class="absolute inset-y-0 right-0 flex w-16 items-center justify-center text-white/60 hover:text-white max-md:hidden"
        aria-label="Next photo"
        data-testid="photo-next"
        @click="go(1)"
      >
        <Icon name="right" :size="6" />
      </button>
    </div>
  </ViewerFrame>
</template>
